<script setup>
import { computed, reactive } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import useVuelidate from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';

const store = useStore();
const router = useRouter();
const { t } = useI18n();

const form = reactive({
  inboxName: '',
  phoneNumber: '',
  senderProfileId: '',
  templateId: '',
});

const rules = computed(() => ({
  inboxName: { required },
  phoneNumber: {
    required,
    shouldStartWithPlusSign: value => value?.startsWith('+'),
  },
  templateId: { required },
}));

const v$ = useVuelidate(rules, form);
const uiFlags = computed(() => store.getters['inboxes/getUIFlags']);

const createChannel = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  try {
    const providerConfig = {
      template_id: form.templateId.trim(),
    };
    if (form.senderProfileId.trim()) {
      providerConfig.sender_profile_id = form.senderProfileId.trim();
    }

    const smsChannel = await store.dispatch('inboxes/createChannel', {
      name: form.inboxName.trim(),
      channel: {
        type: 'sms',
        provider: 'sent',
        phone_number: form.phoneNumber.trim(),
        provider_config: providerConfig,
      },
    });

    router.replace({
      name: 'settings_inboxes_add_agents',
      params: {
        page: 'new',
        inbox_id: smsChannel.id,
      },
    });
  } catch (error) {
    useAlert(t('INBOX_MGMT.ADD.SMS.API.ERROR_MESSAGE'));
  }
};
</script>

<template>
  <form class="flex flex-col gap-4 mt-6" @submit.prevent="createChannel">
    <div
      class="rounded-lg border border-n-weak bg-n-alpha-1 p-4 text-sm text-n-slate-11"
    >
      {{ $t('INBOX_MGMT.ADD.SMS.SENT.HELP') }}
    </div>

    <label :class="{ error: v$.inboxName.$error }">
      {{ $t('INBOX_MGMT.ADD.SMS.SENT.INBOX_NAME.LABEL') }}
      <input
        v-model="form.inboxName"
        type="text"
        :placeholder="$t('INBOX_MGMT.ADD.SMS.SENT.INBOX_NAME.PLACEHOLDER')"
        @blur="v$.inboxName.$touch"
      />
      <span v-if="v$.inboxName.$error" class="message">
        {{ $t('INBOX_MGMT.ADD.SMS.SENT.INBOX_NAME.ERROR') }}
      </span>
    </label>

    <label :class="{ error: v$.phoneNumber.$error }">
      {{ $t('INBOX_MGMT.ADD.SMS.SENT.PHONE_NUMBER.LABEL') }}
      <input
        v-model="form.phoneNumber"
        type="tel"
        :placeholder="$t('INBOX_MGMT.ADD.SMS.SENT.PHONE_NUMBER.PLACEHOLDER')"
        @blur="v$.phoneNumber.$touch"
      />
      <span v-if="v$.phoneNumber.$error" class="message">
        {{ $t('INBOX_MGMT.ADD.SMS.SENT.PHONE_NUMBER.ERROR') }}
      </span>
    </label>

    <label>
      {{ $t('INBOX_MGMT.ADD.SMS.SENT.SENDER_PROFILE_ID.LABEL') }}
      <input
        v-model="form.senderProfileId"
        type="text"
        autocomplete="off"
        :placeholder="
          $t('INBOX_MGMT.ADD.SMS.SENT.SENDER_PROFILE_ID.PLACEHOLDER')
        "
      />
      <span class="message">
        {{ $t('INBOX_MGMT.ADD.SMS.SENT.SENDER_PROFILE_ID.HELP') }}
      </span>
    </label>

    <label :class="{ error: v$.templateId.$error }">
      {{ $t('INBOX_MGMT.ADD.SMS.SENT.TEMPLATE_ID.LABEL') }}
      <input
        v-model="form.templateId"
        type="text"
        autocomplete="off"
        :placeholder="$t('INBOX_MGMT.ADD.SMS.SENT.TEMPLATE_ID.PLACEHOLDER')"
        @blur="v$.templateId.$touch"
      />
      <span v-if="v$.templateId.$error" class="message">
        {{ $t('INBOX_MGMT.ADD.SMS.SENT.TEMPLATE_ID.ERROR') }}
      </span>
      <span class="message">
        {{ $t('INBOX_MGMT.ADD.SMS.SENT.TEMPLATE_ID.HELP') }}
      </span>
    </label>

    <div>
      <NextButton
        :is-loading="uiFlags.isCreating"
        type="submit"
        solid
        blue
        :label="$t('INBOX_MGMT.ADD.SMS.SENT.SUBMIT_BUTTON')"
      />
    </div>
  </form>
</template>
